import React from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@design-system';

import {
  CustomRangeSlider,
  CustomSlider,
  AlertModal,
  BenefitDesignFilters,
  ELIGIBILITY_TOOLTIP_CONTENT,
  useShopInsuranceContext,
  useFilterNoticeDialog,
  DEFAULT_FILTERS_VALUES,
  type PaymentOption,
  type CombinedFilterType,
  type ShopInsuranceFiltersProps,
  type ShopInsuranceMode,
} from '@/features/shop-insurance';

import { useFormContext } from 'react-hook-form';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { formatCurrency } from '@/lib';
import { selectClient } from '@/model';
import { selectFeatureFlags } from '@/model/selectors';
import { UNDERWRITING_STATUS_FILTERS, zipCodeToState } from '@shared';
import { Info } from 'lucide-react';
import { useSelector } from 'react-redux';

export function ShopInsuranceFilters({
  selectedEligibilityFilters = [],
  setSelectedEligibilityFilters,
  showEligibilityFilter = true,
  eligibilityCounts,
  selectedFeatureBenefitsFilters = [],
  setSelectedFeatureBenefitsFilters,
  mode = 'premium-optimization',
  handleModeChange,
  benefitDesignInputs,
  benefitDesignAvailableOptions,
  setMonthlyBenefit,
  setBenefitDuration,
  setInflationProtection,
}: ShopInsuranceFiltersProps = {}) {
  const featureFlags = useSelector(selectFeatureFlags);
  const { flags } = useFeatureFlags();
  const client = useSelector(selectClient);
  const state = zipCodeToState(client.intakeSurvey.clientZipCode);
  const { showNotice, handleNoticeChange, handleConfirm, handleNoticeOpenChange } =
    useFilterNoticeDialog();
  const { setValue, watch, control } = useFormContext();
  const {
    sliderBounds,
    paymentOption,
    handlePaymentOptionChange,
    showTraditionalLTCOptions,
    showSinglePayAnd10Pay,
  } = useShopInsuranceContext();

  const premiumValue = watch('premium');
  const benefitDurationValue: CombinedFilterType = watch('benefitDuration');
  const inflationProtectionValue: CombinedFilterType = watch('inflationProtection');

  // Clamp the displayed slider value to min/max bounds
  const clampedPremiumValue = Math.max(sliderBounds.min, Math.min(sliderBounds.max, premiumValue));

  const showFilters = !['Monthly', 'Annually'].includes(paymentOption);

  const eligibilityFilters = UNDERWRITING_STATUS_FILTERS;

  const InfoTooltip = ({ content }: { content: React.ReactNode }) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <Info className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="border-input bg-background text-secondary-foreground m-1 w-auto max-w-80 whitespace-normal border p-2 text-sm font-normal shadow-md [&>svg]:hidden">
        {content}
      </TooltipContent>
    </Tooltip>
  );

  const handleEligibilityFilterToggle = (filterValue: string) => {
    if (!setSelectedEligibilityFilters) return;

    const updatedFilters = selectedEligibilityFilters.includes(filterValue)
      ? selectedEligibilityFilters.filter(filter => filter !== filterValue)
      : [...selectedEligibilityFilters, filterValue];
    setSelectedEligibilityFilters(updatedFilters);
  };

  const handleFeatureBenefitsFilterToggle = (filterValue: string) => {
    if (!setSelectedFeatureBenefitsFilters) return;

    const updatedFilters = selectedFeatureBenefitsFilters.includes(filterValue)
      ? selectedFeatureBenefitsFilters.filter(filter => filter !== filterValue)
      : [...selectedFeatureBenefitsFilters, filterValue];
    setSelectedFeatureBenefitsFilters(updatedFilters);
  };

  const isBenefitDesign = mode === 'benefit-design';

  return (
    <div className="space-y-5">
      {handleModeChange && (
        <Tabs
          value={mode}
          onValueChange={value => handleModeChange(value as ShopInsuranceMode)}
        >
          <TabsList className="w-full">
            <TabsTrigger
              value="premium-optimization"
              className="flex-1"
            >
              By Premium
            </TabsTrigger>
            <TabsTrigger
              value="benefit-design"
              className="flex-1"
            >
              By Benefit
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {isBenefitDesign &&
        benefitDesignInputs &&
        benefitDesignAvailableOptions &&
        setMonthlyBenefit &&
        setBenefitDuration &&
        setInflationProtection && (
          <BenefitDesignFilters
            inputs={benefitDesignInputs}
            availableOptions={benefitDesignAvailableOptions}
            setMonthlyBenefit={setMonthlyBenefit}
            setBenefitDuration={setBenefitDuration}
            setInflationProtection={setInflationProtection}
          />
        )}

      {!isBenefitDesign && showFilters && (
        <div>
          <h3 className="text-primary-dark mb-3 text-sm font-semibold">Target Premium</h3>
          <div className="space-y-3 px-2">
            <CustomSlider
              name="premium"
              control={control}
              max={sliderBounds.max}
              min={sliderBounds.min}
              step={1000}
              defaultValue={sliderBounds.defaultValue}
              formatValue={value => formatCurrency(value)}
              hideResetButton={false}
              defaultLabel="Reset"
              label={formatCurrency(clampedPremiumValue)}
              showCustomInput={flags.customPremiumInput ?? false}
              customInputLabel="Enter custom amount"
              data-testid="shop-insurance-filters-premium-min"
            />
          </div>
        </div>
      )}
      {!isBenefitDesign && (
        <div>
          <h3 className="text-primary-dark mb-2 text-sm font-semibold">Payment Option</h3>
          <Select
            value={paymentOption}
            onValueChange={value => handlePaymentOptionChange(value as PaymentOption)}
          >
            <SelectTrigger
              className="w-full"
              data-testid={`shop-insurance-filters-payment-option`}
            >
              <SelectValue placeholder="Select payment option" />
            </SelectTrigger>
            <SelectContent>
              {showSinglePayAnd10Pay && (
                <>
                  <SelectItem value="Single Pay">Single Pay</SelectItem>
                  <SelectItem value="10-Pay">10-Pay</SelectItem>
                </>
              )}
              {showTraditionalLTCOptions && (
                <>
                  <SelectItem value="Annually">Annually</SelectItem>
                  <SelectItem value="Monthly">Monthly</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>
      )}
      {!isBenefitDesign && showFilters && (
        <div className="border-border border-t pt-5">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="text-primary-dark text-sm font-semibold">Inflation Protection</h3>
            <Select
              value={inflationProtectionValue.type}
              onValueChange={value =>
                setValue('inflationProtection', { ...inflationProtectionValue, type: value })
              }
            >
              <SelectTrigger
                size="sm"
                data-testid="shop-insurance-filters-inflation-protection-select"
                className="h-8! w-max px-2 py-1.5 text-xs [&>svg]:size-3.5"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="combined">Combined</SelectItem>
                <SelectItem value="separate">Separate AOB/COB</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {inflationProtectionValue.type === 'combined' ? (
            <CustomRangeSlider
              showTooltip
              min={0}
              max={5}
              step={1}
              control={control}
              name="inflationProtection.combined"
              tooltipContent={value => `${value}%`}
              defaultValue={DEFAULT_FILTERS_VALUES.inflationProtection}
              onValueChange={() => handleNoticeChange('inflationProtection')}
              label={`${inflationProtectionValue.combined[0]}% - ${inflationProtectionValue.combined[1]}%`}
              testId="shop-insurance-filters-inflation-protection-combined"
            />
          ) : (
            <div className="flex w-full gap-4">
              <div className="w-full">
                <h4 className="text-primary-dark mb-2 text-xs font-medium">AOB</h4>
                <CustomRangeSlider
                  showTooltip
                  min={0}
                  max={5}
                  step={1}
                  size="sm"
                  control={control}
                  name="inflationProtection.aob"
                  tooltipContent={value => `${value}%`}
                  defaultValue={DEFAULT_FILTERS_VALUES.inflationProtection}
                  onValueChange={() => handleNoticeChange('inflationProtection')}
                  label={`${inflationProtectionValue.aob[0]}% - ${inflationProtectionValue.aob[1]}%`}
                  testId="shop-insurance-filters-inflation-protection-aob"
                />
              </div>
              <div className="w-full">
                <h4 className="text-primary-dark mb-2 text-xs font-medium">COB</h4>
                <CustomRangeSlider
                  showTooltip
                  min={0}
                  max={5}
                  step={1}
                  size="sm"
                  control={control}
                  name="inflationProtection.cob"
                  tooltipContent={value => `${value}%`}
                  defaultValue={DEFAULT_FILTERS_VALUES.inflationProtection}
                  onValueChange={() => handleNoticeChange('inflationProtection')}
                  label={`${inflationProtectionValue.cob[0]}% - ${inflationProtectionValue.cob[1]}%`}
                  testId="shop-insurance-filters-inflation-protection-cob"
                />
              </div>
            </div>
          )}
        </div>
      )}
      {!isBenefitDesign && showFilters && (
        <div className="border-border border-t pt-5">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="text-primary-dark text-sm font-semibold">Benefit Duration</h3>
            <Select
              value={benefitDurationValue.type}
              onValueChange={value =>
                setValue('benefitDuration', { ...benefitDurationValue, type: value })
              }
            >
              <SelectTrigger className="h-8! w-max px-2 py-1.5 text-xs [&>svg]:size-3.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="combined">Total Duration</SelectItem>
                <SelectItem value="separate">Separate AOB/COB</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {benefitDurationValue.type === 'combined' ? (
            <CustomRangeSlider
              showTooltip
              min={1}
              max={8}
              step={1}
              control={control}
              name="benefitDuration.combined"
              tooltipContent={value => `${value} Year${value !== 1 ? 's' : ''}`}
              defaultValue={DEFAULT_FILTERS_VALUES.benefitDuration}
              onValueChange={() => handleNoticeChange('benefitDuration')}
              label={`${benefitDurationValue.combined[0]} - ${benefitDurationValue.combined[1]} Years`}
              testId="shop-insurance-filters-benefit-duration-combined"
            />
          ) : (
            <div className="flex w-full gap-4">
              <div className="w-full">
                <h4 className="text-primary-dark mb-2 text-xs font-medium">AOB</h4>
                <CustomRangeSlider
                  showTooltip
                  size="sm"
                  min={1}
                  step={1}
                  max={8}
                  control={control}
                  name="benefitDuration.aob"
                  tooltipContent={value => `${value} Year${value !== 1 ? 's' : ''}`}
                  defaultValue={DEFAULT_FILTERS_VALUES.benefitDuration}
                  onValueChange={() => handleNoticeChange('benefitDuration')}
                  label={`${benefitDurationValue.aob[0]} - ${benefitDurationValue.aob[1]} Years`}
                  testId="shop-insurance-filters-benefit-duration-aob"
                />
              </div>
              <div className="w-full">
                <h4 className="text-primary-dark mb-2 text-xs font-medium">COB</h4>
                <CustomRangeSlider
                  size="sm"
                  showTooltip
                  min={1}
                  step={1}
                  max={8}
                  control={control}
                  name="benefitDuration.cob"
                  tooltipContent={value => `${value} Year${value !== 1 ? 's' : ''}`}
                  defaultValue={DEFAULT_FILTERS_VALUES.benefitDuration}
                  onValueChange={() => handleNoticeChange('benefitDuration')}
                  label={`${benefitDurationValue.cob[0]} - ${benefitDurationValue.cob[1]} Years`}
                  testId="shop-insurance-filters-benefit-duration-cob"
                />
              </div>
            </div>
          )}
        </div>
      )}
      {!isBenefitDesign && setSelectedEligibilityFilters && showEligibilityFilter && (
        <div className="border-border border-t pt-5">
          <h3 className="text-primary-dark mb-3 text-sm font-semibold">Eligibility Filter</h3>
          <div className="flex flex-col gap-3">
            {eligibilityFilters.map(filter => {
              const count =
                eligibilityCounts?.[filter.value as keyof typeof eligibilityCounts] || 0;
              return (
                <div
                  key={filter.value}
                  className="flex items-center gap-3"
                >
                  <Checkbox
                    data-testid={`shop-insurance-filters-eligibility-${filter.value}`}
                    id={filter.value}
                    checked={selectedEligibilityFilters.includes(filter.value)}
                    onCheckedChange={() => handleEligibilityFilterToggle(filter.value)}
                    className="data-[state=checked]:bg-primary data-[state=checked]:border-primary border-input h-4 w-4 rounded border-2"
                  />
                  <label
                    htmlFor={filter.value}
                    className="text-foreground flex flex-1 cursor-pointer items-center gap-1.5 text-sm font-medium"
                  >
                    {filter.label}
                    <span className="text-foreground text-xs">({count})</span>
                  </label>
                  <InfoTooltip content={ELIGIBILITY_TOOLTIP_CONTENT[filter.value] || ''} />
                </div>
              );
            })}
          </div>
        </div>
      )}
      {!isBenefitDesign &&
        featureFlags.quoteApplySecurianFinancial &&
        !['CA', 'NY'].includes(state.stateAbbreviation) && (
          <div className="border-border border-t pt-5">
            <h3 className="text-primary-dark mb-3 text-sm font-semibold">Features & Benefits</h3>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  data-testid="shop-insurance-filters-global-benefits"
                  id="hasGlobalBenefits"
                  checked={selectedFeatureBenefitsFilters.includes('hasGlobalBenefits')}
                  onCheckedChange={() => handleFeatureBenefitsFilterToggle('hasGlobalBenefits')}
                />
                <label
                  htmlFor="hasGlobalBenefits"
                  className="text-foreground flex flex-1 cursor-pointer items-center gap-1.5 text-sm font-medium"
                >
                  International Benefits
                </label>
              </div>
            </div>
          </div>
        )}
      <AlertModal
        title="Are you sure?"
        open={showNotice}
        onOpenChange={handleNoticeOpenChange}
        onConfirm={handleConfirm}
      >
        By adjusting this filter you are reducing the number of policies analyzed, which may filter
        out policies with a high modeled ROI, leaving you with lower modeled ROI options. Are you
        sure you want to restrict the search?
      </AlertModal>
    </div>
  );
}
